import { checkedTypes } from "constants/types";
import { folderBy } from "layout/dictionarySearch/constants";

const expandCategories = (categories, payload) =>
  categories.map((category) => {
    if (category.index === payload.index)
      return { ...category, expanded: payload.expanded };
    return category;
  });

const byFolderSetExpandedTo = (state, payload) => {
  const { categories } = state.folder.dictionary;
  const expandedCategories = expandCategories(categories, payload);

  state.folder.dictionary.categories = expandedCategories;
};

const byDocumentSetExpandedTo = (state, payload) => {
  state.documents = state.documents.map((d) => {
    if (d.id === payload.docId) {
      return {
        ...d,
        dictionary: {
          ...d.dictionary,
          categories: expandCategories(d.dictionary.categories, payload),
        },
      };
    }
    return d;
  });
};

export const setExpandedTo = (state, action) => {
  if (state.config.folderBy === folderBy.folder)
    byFolderSetExpandedTo(state, action.payload);
  if (state.config.folderBy === folderBy.document)
    byDocumentSetExpandedTo(state, action.payload);
};

export const setDocumentCheckState = (state, { payload }) => {
  state.documents = state.documents.map((d) => {
    if (d.id === payload.docId) {
      const isChecked = payload.checkState === checkedTypes.CHECKED;
      return {
        ...d,
        checkState: payload.checkState,
        expanded: isChecked || d.expanded,
        dictionary: {
          ...d.dictionary,
          categories: d.dictionary.categories.map((c) => ({
            ...c,
            expanded: isChecked || c.expanded,
            checkState:
              isChecked && c.occurrences
                ? checkedTypes.CHECKED
                : checkedTypes.EMPTY,
            items: c.items?.map((i) => ({
              ...i,
              checkState:
                isChecked && i.occurrences
                  ? checkedTypes.CHECKED
                  : checkedTypes.EMPTY,
            })),
          })),
        },
      };
    }
    return d;
  });
};

const checkCategories = (categories, payload) => {
  const isChecked = payload.checkedState === checkedTypes.CHECKED;

  return categories.map((category) => {
    if (category.index === payload.index) {
      return {
        ...category,
        expanded: isChecked || category.expanded,
        checkState: payload.checkedState,
        items: category.items.map((item) => ({
          ...item,
          checkState:
            isChecked && item.occurrences
              ? checkedTypes.CHECKED
              : checkedTypes.EMPTY,
        })),
      };
    }

    return category;
  });
};

const byFolderSetCategoryCheckState = (state, payload) => {
  const { categories } = state.folder.dictionary;
  state.folder.dictionary.categories = checkCategories(categories, payload);
};

const getCheckState = (categories) => {
  const checkedCategories = categories.filter(
    (c) => c.checkState === checkedTypes.CHECKED && c.occurrences > 0
  );
  const unCheckedCategories = categories.filter(
    (c) =>
      (c.checkState === checkedTypes.EMPTY || c.checkState === undefined) &&
      c.occurrences > 0
  );

  if (
    checkedCategories.length &&
    checkedCategories.length ===
      categories.filter((c) => c.occurrences > 0).length
  ) {
    return checkedTypes.CHECKED;
  }

  if (
    unCheckedCategories.length ===
    categories.filter((c) => c.occurrences > 0).length
  ) {
    return checkedTypes.EMPTY;
  }

  return checkedTypes.INDETERMINATE;
};

export const updateDocCheckState = (state) => {
  state.documents = state.documents.map((d) => ({
    ...d,
    checkState: getCheckState(d.dictionary.categories),
  }));
};

const byDocumentSetCategoryCheckState = (state, payload) => {
  state.documents = state.documents.map((d) => {
    if (d.id === payload.docId) {
      return {
        ...d,
        dictionary: {
          ...d.dictionary,
          categories: checkCategories(d.dictionary.categories, payload),
        },
      };
    }
    return d;
  });

  updateDocCheckState(state);
};

export const setCategoryCheckState = (state, { payload }) => {
  if (state.config.folderBy === folderBy.folder)
    byFolderSetCategoryCheckState(state, payload);
  if (state.config.folderBy === folderBy.document)
    byDocumentSetCategoryCheckState(state, payload);
};

const updateCategories = (categories) =>
  categories.map((category) => {
    const hasCheckedItem =
      category.items
        ?.filter((i) => i.occurrences > 0)
        .find((i) => i.checkState === checkedTypes.CHECKED) !== undefined;

    const hasUncheckedItem =
      category.items
        ?.filter((i) => i.occurrences > 0)
        .find(
          (i) =>
            i.checkState === checkedTypes.EMPTY || i.checkState === undefined
        ) !== undefined;

    let categoryCheckedState;

    if (hasCheckedItem && hasUncheckedItem) {
      categoryCheckedState = checkedTypes.INDETERMINATE;
    }
    if (hasCheckedItem && !hasUncheckedItem) {
      categoryCheckedState = checkedTypes.CHECKED;
    }
    if (!hasCheckedItem && hasUncheckedItem) {
      categoryCheckedState = checkedTypes.EMPTY;
    }
    return { ...category, checkState: categoryCheckedState };
  });

const checkItem = (categories, payload) =>
  categories.map((category) => ({
    ...category,
    items: category.items?.map((i) => {
      if (i.index === payload.index)
        return { ...i, checkState: payload.checkedState };
      return i;
    }),
  }));

const byFolderCheckItemAndUpdateCategories = (state, payload) => {
  const { categories } = state.folder.dictionary;

  state.folder.dictionary.categories = updateCategories(
    checkItem(categories, payload)
  );
};

const byDocumentSetCheckedItemState = (state, payload) => {
  state.documents = state.documents.map((d) => {
    if (d.id === payload.docId) {
      return {
        ...d,
        dictionary: {
          ...d.dictionary,
          categories: updateCategories(
            checkItem(d.dictionary.categories, payload)
          ),
        },
      };
    }
    return d;
  });

  updateDocCheckState(state);
};

export const setItemCheckState = (state, { payload }) => {
  if (state.config.folderBy === folderBy.folder)
    byFolderCheckItemAndUpdateCategories(state, payload);
  if (state.config.folderBy === folderBy.document)
    byDocumentSetCheckedItemState(state, payload);
};

const checkDocumentInState = (state, payload) => {
  state.folder.documents = state.folder.documents.map((d) => {
    if (d.id === payload.id) {
      return { ...d, checkState: payload.checkState };
    }
    return d;
  });
};

const reCalcFrequencies = (documents, uncheckedDocsIds) => {
  let result = 0;
  documents.forEach((d) => {
    if (!uncheckedDocsIds.includes(d.id)) result += d.occurrences;
  });
  return result;
};

const reCalculateCategoriesAndItems = (state, uncheckedDocsIds) => {
  state.folder.dictionary.categories = state.folder.dictionary.categories.map(
    (c) => ({
      ...c,
      occurrences: reCalcFrequencies(c.documents, uncheckedDocsIds),
      items: c.items?.map((i) => ({
        ...i,
        occurrences: reCalcFrequencies(i.documents, uncheckedDocsIds),
      })),
    })
  );
};

export const setDocCheckState = (state, { payload }) => {
  checkDocumentInState(state, payload);

  const uncheckedDocsIds = state.folder.documents
    .filter((d) => d.checkState === checkedTypes.EMPTY)
    .map((d) => d.id);

  reCalculateCategoriesAndItems(state, uncheckedDocsIds);
};

export const setDocExpandState = (state, { payload }) => {
  state.documents = state.documents.map((d) => {
    if (d.id === payload) return { ...d, expanded: !d.expanded };
    return d;
  });
};

const byFolderSetAllExpandedTo = (state, expanded) => {
  state.folder.dictionary.categories = state.folder.dictionary.categories.map(
    (c) => ({
      ...c,
      expanded,
      items: c.items?.map((i) => ({ ...i, expanded })),
    })
  );
};

const byDocumentSetAllExpandedTo = (state, expanded) => {
  state.documents = state.documents.map((d) => {
    return {
      ...d,
      expanded,
      dictionary: {
        ...d.dictionary,
        categories: d.dictionary.categories.map((c) => ({ ...c, expanded })),
      },
    };
  });
};

export const setAllExpandedTo = (state, action) => {
  const expanded = action.payload;
  if (state.config.folderBy === folderBy.folder)
    byFolderSetAllExpandedTo(state, expanded);
  if (state.config.folderBy === folderBy.document)
    byDocumentSetAllExpandedTo(state, expanded);
};

const byFolderSetAllCheckStateTo = (state, checkState) => {
  state.folder.dictionary.categories = state.folder.dictionary.categories.map(
    (c) => ({
      ...c,
      checkState,
      expanded: checkState === checkedTypes.CHECKED ? true : c.expanded,
      items: c.items?.map((i) => ({ ...i, checkState })),
    })
  );
};

const byDocumentSetAllCheckStateTo = (state, checkState) => {
  state.documents = state.documents.map((d) => {
    return {
      ...d,
      expanded: checkState === checkedTypes.CHECKED ? true : d.expanded,
      checkState,
      dictionary: {
        ...d.dictionary,
        categories: d.dictionary.categories.map((c) => ({
          ...c,
          expanded: checkState === checkedTypes.CHECKED ? true : d.expanded,
          checkState,
          items: c.items?.map((i) => ({ ...i, checkState })),
        })),
      },
    };
  });
};

export const setAllCheckedStateTo = (state, action) => {
  const checkState = action.payload;
  if (state.config.folderBy === folderBy.folder)
    byFolderSetAllCheckStateTo(state, checkState);
  if (state.config.folderBy === folderBy.document)
    byDocumentSetAllCheckStateTo(state, checkState);
};

export const setCategories = (state, action) => {
  if (state.config.folderBy === folderBy.folder) {
    state.folder = action.payload;
  }
  if (state.config.folderBy === folderBy.document) {
    state.documents = action.payload;
  }
};
